// src/components/alerts/ActiveAlertsTable.js
// ═══════════════════════════════════════════════════════════════════════════
// Enhanced Active Alerts Table — All existing logic preserved + new features
// ═══════════════════════════════════════════════════════════════════════════
// --- UPDATE (Alert System AI) ------------------------------------------------
//   • Selection can be controlled by the page (selectedIds / onToggleSelect / onToggleSelectAll),
//     so the bulk Acknowledge / Resolve bar finally sees what is ticked. Without those props
//     the table keeps its own selection as before.
//   • NEW "AI" column: FlowSense AI triage score (0–100) with its reasons on hover (triageById).
//   • highlightIds: rows FlowSense AI pointed at, or that just arrived live, are highlighted.
//   • Work-order link no longer crashes when work_order_id is a number (substring on a number).
// --- END UPDATE --------------------------------------------------------------

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Table, Thead, Tbody, Tr, Th, Td, Badge, Button, ButtonGroup, Icon, Text,
    useColorModeValue, IconButton, Flex, Box, Link as ChakraLink, Tooltip,
    Checkbox, Menu, MenuButton, MenuList, MenuItem, HStack, VStack,
    useToast, Tag, TagLabel, Progress, Divider,
} from '@chakra-ui/react';
import {
    MdVisibility, MdEditNote, MdCheckCircle, MdError, MdLink, MdSensors,
    MdBuild, MdContentCopy, MdMoreVert, MdFileDownload, MdWarning,
    MdInfo, MdTrendingUp, MdSchedule, MdNotifications,
} from 'react-icons/md';
import { Link as RouterLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDate } from '../../utils/date';

const MotionTr = motion(Tr); // --- UPDATE (Alert System AI): was motion.tr, which ignored Chakra props (bg / _hover) and got 'blue.50' as a raw CSS colour
const MotionBox = motion(Box);

// ═════════════════════════════════════════════════════════════════════════════
// ANIMATION VARIANTS
// ═════════════════════════════════════════════════════════════════════════════
const rowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
        opacity: 1,
        x: 0,
        transition: {
            delay: i * 0.05,
            duration: 0.3,
        },
    }),
    exit: { opacity: 0, x: 20, transition: { duration: 0.2 } },
};

const pulseVariants = {
    pulse: {
        scale: [1, 1.05, 1],
        transition: {
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
        },
    },
};

// ═════════════════════════════════════════════════════════════════════════════
// PRIORITY INDICATOR COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const PriorityIndicator = ({ severity }) => {
    const config = {
        critical: { color: 'red.500', icon: MdError, label: 'Critical', pulse: true },
        high: { color: 'orange.500', icon: MdWarning, label: 'High', pulse: false },
        medium: { color: 'yellow.500', icon: MdInfo, label: 'Medium', pulse: false },
        low: { color: 'green.500', icon: MdCheckCircle, label: 'Low', pulse: false },
    };

    const cfg = config[severity?.toLowerCase()] || config.low;

    return (
        <Tooltip label={`${cfg.label} Priority`} hasArrow>
            <MotionBox
                display="inline-block"
                variants={cfg.pulse ? pulseVariants : {}}
                animate={cfg.pulse ? "pulse" : ""}
            >
                <Icon as={cfg.icon} color={cfg.color} boxSize={4} />
            </MotionBox>
        </Tooltip>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// TIME AGO COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const TimeAgo = ({ timestamp }) => {
    if (!timestamp) return <Text fontSize="xs" color="gray.400">N/A</Text>;

    const getTimeAgo = () => {
        const now = new Date();
        const then = new Date(timestamp);
        const diffMs = now - then;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${diffDays}d ago`;
    };

    const timeAgo = getTimeAgo();
    const color = timeAgo === 'Just now' ? 'green.500' : 'gray.500';

    return (
        <HStack spacing={1}>
            <Icon as={MdSchedule} boxSize={3} color={color} />
            <Text fontSize="xs" color={color} fontWeight="600">
                {timeAgo}
            </Text>
        </HStack>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
function ActiveAlertsTable({ alerts, onAcknowledge, onResolve, onViewDetails, isLoading, selectedIds, onToggleSelect, onToggleSelectAll, triageById, highlightIds }) {
    const [internalSelected, setSelectedAlerts] = useState(new Set());
    // --- UPDATE (Alert System AI): controlled selection when the page provides it
    const isControlled = !!(selectedIds && onToggleSelect);
    const selectedAlerts = isControlled ? selectedIds : internalSelected;
    const highlightBg = useColorModeValue('purple.50', 'purple.900');
    // --- END UPDATE
    const [sortField, setSortField] = useState('triggered_at');
    const [sortDirection, setSortDirection] = useState('desc');
    
    const toast = useToast();

    // --- UPDATE: Fix Hook Rules - ALL hooks must be called at the very top ---
    const textColor = useColorModeValue("gray.700", "white");
    const tableRowHoverBg = useColorModeValue("gray.50", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const headerBg = useColorModeValue("gray.100", "gray.750");
    const selectedRowBg = useColorModeValue("blue.50", "blue.900");
    const selectionBannerBg = useColorModeValue("blue.50", "blue.900"); 
    const sensorIconBg = useColorModeValue('teal.50', 'teal.900');      
    // --- END UPDATE ---

    // Helper functions
    const getSeverityColorScheme = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'critical': return 'red';
            case 'high': return 'orange';
            case 'medium': return 'yellow';
            case 'low': return 'green';
            default: return 'gray';
        }
    };

    const getStatusColorScheme = (status) => {
        switch (status?.toLowerCase()) {
            case 'active': return 'red';
            case 'acknowledged': return 'blue';
            case 'work_order_created': return 'purple';
            case 'resolved':
            case 'auto_resolved':
            case 'resolved_by_work_order': return 'green';
            default: return 'gray';
        }
    };

    // Deduplication logic
    const processedAlerts = useMemo(() => {
        if (!alerts) return [];
        
        const activeMap = new Map();
        const displayList = [];

        alerts.forEach(alert => {
            if (alert.status === 'active') {
                const dedupKey = `${alert.sensor_id}-${alert.alert_message}`;
                
                if (activeMap.has(dedupKey)) {
                    activeMap.get(dedupKey).duplicate_count += 1;
                } else {
                    const newAlertObj = { ...alert, duplicate_count: 0 };
                    activeMap.set(dedupKey, newAlertObj);
                    displayList.push(newAlertObj);
                }
            } else {
                displayList.push(alert);
            }
        });

        return displayList;
    }, [alerts]);

    // Sorting logic
    const sortedAlerts = useMemo(() => {
        if (!processedAlerts) return [];
        
        return [...processedAlerts].sort((a, b) => {
            let aVal, bVal;
            
            switch (sortField) {
                case 'severity':
                    const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
                    aVal = severityOrder[a.severity?.toLowerCase()] || 0;
                    bVal = severityOrder[b.severity?.toLowerCase()] || 0;
                    break;
                case 'triggered_at':
                    aVal = new Date(a.triggered_at || 0).getTime();
                    bVal = new Date(b.triggered_at || 0).getTime();
                    break;
                case 'sensor_id':
                    aVal = a.sensor_id || '';
                    bVal = b.sensor_id || '';
                    break;
                default:
                    return 0;
            }
            
            if (sortDirection === 'asc') {
                return aVal > bVal ? 1 : -1;
            }
            return aVal < bVal ? 1 : -1;
        });
    }, [processedAlerts, sortField, sortDirection]);

    // Selection handlers
    const toggleSelectAll = () => {
        if (isControlled) { (onToggleSelectAll || (() => {}))(sortedAlerts.map(a => a.id)); return; } // --- UPDATE (Alert System AI)
        if (selectedAlerts.size === sortedAlerts.length) {
            setSelectedAlerts(new Set());
        } else {
            setSelectedAlerts(new Set(sortedAlerts.map(a => a.id)));
        }
    };

    const toggleSelect = (alertId) => {
        if (isControlled) { onToggleSelect(alertId); return; } // --- UPDATE (Alert System AI)
        setSelectedAlerts(prev => {
            const next = new Set(prev);
            if (next.has(alertId)) {
                next.delete(alertId);
            } else {
                next.add(alertId);
            }
            return next;
        });
    };

    // Copy alert details to clipboard
    const copyAlertDetails = (alert) => {
        const details = `
Alert ID: ${alert.id}
Sensor: ${alert.sensor_id} (${alert.sensor_name || 'N/A'})
Message: ${alert.alert_message}
Severity: ${alert.severity}
Status: ${alert.status}
Triggered: ${formatDate(alert.triggered_at, true)}
        `.trim();

        navigator.clipboard.writeText(details);
        toast({
            title: 'Copied to clipboard',
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    // Export single alert
    const exportAlert = (alert) => {
        const json = JSON.stringify(alert, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `alert-${alert.id}.json`;
        a.click();
        toast({
            title: 'Alert exported',
            status: 'success',
            duration: 2000,
        });
    };

    // Sort handler
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    // EARLY RETURNS MUST BE AFTER HOOKS
    if (isLoading && (!sortedAlerts || sortedAlerts.length === 0)) {
        return (
            <Box py={8} textAlign="center">
                <VStack spacing={3}>
                    <Progress size="xs" isIndeterminate colorScheme="blue" w="200px" borderRadius="full" />
                    <Text color="gray.500">Loading alerts...</Text>
                </VStack>
            </Box>
        );
    }

    if (!sortedAlerts || sortedAlerts.length === 0) {
        return (
            <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                py={12}
                textAlign="center"
            >
                <VStack spacing={3}>
                    <Icon as={MdNotifications} boxSize={12} color="gray.300" />
                    <Text fontWeight="600" fontSize="lg" color="gray.500">
                        No active or recent sensor alerts
                    </Text>
                    <Text fontSize="sm" color="gray.400">
                        Alerts will appear here when triggered by your rules
                    </Text>
                </VStack>
            </MotionBox>
        );
    }

    return (
        <Box>
            {/* Batch selection info */}
            <AnimatePresence>
                {!isControlled && selectedAlerts.size > 0 && ( /* --- UPDATE (Alert System AI): the page shows its own bulk bar */
                    <MotionBox
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        mb={3}
                    >
                        <Flex
                            align="center"
                            justify="space-between"
                            p={3}
                            bg={selectionBannerBg}
                            borderRadius="lg"
                            borderWidth="1px"
                            borderColor="blue.200"
                        >
                            <HStack>
                                <Badge colorScheme="blue" fontSize="md" px={3} py={1} borderRadius="full">
                                    {selectedAlerts.size} selected
                                </Badge>
                                <Text fontSize="sm" color="gray.600">
                                    Batch operations available
                                </Text>
                            </HStack>
                            <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() => setSelectedAlerts(new Set())}
                            >
                                Clear selection
                            </Button>
                        </Flex>
                    </MotionBox>
                )}
            </AnimatePresence>

            {/* Table */}
            <Box overflowX="auto" borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                <Table variant="simple" color={textColor} size="sm">
                    <Thead bg={headerBg}>
                        <Tr>
                            <Th w="40px">
                                <Checkbox
                                    aria-label="Select all alerts on this page"
                                    isChecked={sortedAlerts.length > 0 && sortedAlerts.every(a => selectedAlerts.has(a.id))}
                                    isIndeterminate={sortedAlerts.some(a => selectedAlerts.has(a.id)) && !sortedAlerts.every(a => selectedAlerts.has(a.id))}
                                    onChange={toggleSelectAll}
                                    colorScheme="blue"
                                />
                            </Th>
                            <Th w="40px">
                                <Icon as={MdTrendingUp} boxSize={4} color="gray.500" />
                            </Th>
                            {triageById && <Th w="56px"><Tooltip hasArrow label="FlowSense AI priority (0–100)"><Text>AI</Text></Tooltip></Th>} {/* --- UPDATE (Alert System AI) */}
                            <Th cursor="pointer" onClick={() => handleSort('triggered_at')} _hover={{ color: 'blue.500' }}>
                                <HStack spacing={1}>
                                    <Text>ID / Triggered</Text>
                                    {sortField === 'triggered_at' && (
                                        <Icon as={sortDirection === 'asc' ? MdTrendingUp : MdTrendingUp} transform={sortDirection === 'desc' ? 'rotate(180deg)' : ''} />
                                    )}
                                </HStack>
                            </Th>
                            <Th cursor="pointer" onClick={() => handleSort('sensor_id')} _hover={{ color: 'blue.500' }}>
                                <HStack spacing={1}>
                                    <Text>Sensor / Asset</Text>
                                    {sortField === 'sensor_id' && (
                                        <Icon as={MdTrendingUp} transform={sortDirection === 'desc' ? 'rotate(180deg)' : ''} />
                                    )}
                                </HStack>
                            </Th>
                            <Th>Message</Th>
                            <Th cursor="pointer" onClick={() => handleSort('severity')} _hover={{ color: 'blue.500' }}>
                                <HStack spacing={1}>
                                    <Text>Severity</Text>
                                    {sortField === 'severity' && (
                                        <Icon as={MdTrendingUp} transform={sortDirection === 'desc' ? 'rotate(180deg)' : ''} />
                                    )}
                                </HStack>
                            </Th>
                            <Th>Status</Th>
                            <Th>Rule</Th>
                            <Th>Work Order</Th>
                            <Th>Actions</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        <AnimatePresence>
                            {sortedAlerts.map((alert, index) => {
                                const isSelected = selectedAlerts.has(alert.id);
                                
                                return (
                                    <MotionTr
                                        key={alert.id}
                                        custom={index}
                                        variants={rowVariants}
                                        initial="hidden"
                                        animate="visible"
                                        exit="exit"
                                        // --- UPDATE (Alert System AI): Chakra colours work now; AI-highlighted / live rows tinted
                                        bg={isSelected ? selectedRowBg : (highlightIds && highlightIds.has(alert.id) ? highlightBg : 'transparent')}
                                        _hover={{ bg: tableRowHoverBg }}
                                        // --- END UPDATE
                                    >
                                        {/* Selection checkbox */}
                                        <Td>
                                            <Checkbox
                                                aria-label={`Select alert ${alert.id}`}
                                                isChecked={isSelected}
                                                onChange={() => toggleSelect(alert.id)}
                                                colorScheme="blue"
                                            />
                                        </Td>

                                        {/* Priority indicator */}
                                        <Td>
                                            <PriorityIndicator severity={alert.severity} />
                                        </Td>
                                        {/* --- UPDATE (Alert System AI): AI triage score */}
                                        {triageById && (
                                            <Td>
                                                {triageById[alert.id] ? (
                                                    <Tooltip hasArrow label={(triageById[alert.id].reasons || []).join(' · ')}>
                                                        <Badge colorScheme={triageById[alert.id].score >= 70 ? 'red' : triageById[alert.id].score >= 45 ? 'orange' : triageById[alert.id].score >= 25 ? 'yellow' : 'green'} borderRadius="md" fontSize="sm">
                                                            {triageById[alert.id].score}
                                                        </Badge>
                                                    </Tooltip>
                                                ) : <Text fontSize="xs" color="gray.400">—</Text>}
                                            </Td>
                                        )}
                                        {/* --- END UPDATE */}

                                        {/* ID / Triggered */}
                                        <Td>
                                            <VStack align="start" spacing={0}>
                                                <Text fontWeight="bold" fontSize="sm">#{alert.id}</Text>
                                                <TimeAgo timestamp={alert.triggered_at} />
                                                <Text fontSize="xs" color="gray.400">
                                                    {formatDate(alert.triggered_at, true) || 'N/A'}
                                                </Text>
                                            </VStack>
                                        </Td>

                                        {/* Sensor / Asset */}
                                        <Td>
                                            <Flex align="center" gap={2}>
                                                <Box
                                                    p={2}
                                                    bg={sensorIconBg}
                                                    borderRadius="md"
                                                >
                                                    <Icon as={MdSensors} color="teal.500" boxSize={4} />
                                                </Box>
                                                <Box>
                                                    <Text fontSize="sm" fontWeight="600">
                                                        {alert.sensor_name || alert.sensor_id}
                                                    </Text>
                                                    <Text fontSize="xs" color={alert.sensor_location ? "gray.400" : "orange.500"}>
                                                        {alert.sensor_location || 'Location placement needed for sensor'}
                                                    </Text>
                                                    {alert.asset_id && (
                                                        <Text fontSize="xs" color="blue.500" mt={1}>
                                                            Asset: {alert.asset_location_description || alert.asset_type_name || alert.asset_id}
                                                        </Text>
                                                    )}
                                                </Box>
                                            </Flex>
                                        </Td>

                                        {/* Message */}
                                        <Td maxW="350px">
                                            <VStack align="start" spacing={1}>
                                                <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
                                                    {alert.alert_message}
                                                </Text>
                                                {alert.last_violation_value !== null && (
                                                    <Tag size="sm" colorScheme="blue" variant="subtle">
                                                        <TagLabel>Value: {alert.last_violation_value}</TagLabel>
                                                    </Tag>
                                                )}
                                                {alert.duplicate_count > 0 && (
                                                    <MotionBox
                                                        initial={{ scale: 0.8, opacity: 0 }}
                                                        animate={{ scale: 1, opacity: 1 }}
                                                    >
                                                        <Badge colorScheme="red" variant="solid" fontSize="xs">
                                                            +{alert.duplicate_count} Duplicate Events Hidden
                                                        </Badge>
                                                    </MotionBox>
                                                )}
                                            </VStack>
                                        </Td>

                                        {/* Severity */}
                                        <Td>
                                            <Badge
                                                colorScheme={getSeverityColorScheme(alert.severity)}
                                                fontSize="xs"
                                                px={3}
                                                py={1}
                                                borderRadius="full"
                                                fontWeight="700"
                                            >
                                                {alert.severity}
                                            </Badge>
                                        </Td>

                                        {/* Status */}
                                        <Td>
                                            <VStack align="start" spacing={1}>
                                                <Badge
                                                    colorScheme={getStatusColorScheme(alert.status)}
                                                    fontSize="xs"
                                                    px={3}
                                                    py={1}
                                                    borderRadius="full"
                                                    fontWeight="700"
                                                >
                                                    {alert.status?.replace('_', ' ') || 'Unknown'}
                                                </Badge>
                                                {alert.acknowledged_at && (
                                                    <Text fontSize="xs" color="gray.400">
                                                        Ack: {formatDate(alert.acknowledged_at, true)}
                                                    </Text>
                                                )}
                                                {alert.resolved_at && (
                                                    <Text fontSize="xs" color="gray.400">
                                                        Res: {formatDate(alert.resolved_at, true)}
                                                    </Text>
                                                )}
                                            </VStack>
                                        </Td>

                                        {/* Rule */}
                                        <Td>
                                            {alert.rule_id ? (
                                                <VStack align="start" spacing={0}>
                                                    <Text fontSize="xs" fontWeight="600">
                                                        Rule #{alert.rule_id}
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.500">
                                                        {alert.rule_name || 'N/A'}
                                                    </Text>
                                                </VStack>
                                            ) : (
                                                <Text fontSize="xs" fontStyle="italic" color="gray.500">
                                                    Manual/Other
                                                </Text>
                                            )}
                                        </Td>

                                        {/* Work Order */}
                                        <Td>
                                            {alert.work_order_id ? (
                                                <ChakraLink
                                                    as={RouterLink}
                                                    to={`/admin/workorders?search=${alert.work_order_id}`}
                                                    color="teal.500"
                                                    _hover={{ color: 'teal.600', textDecoration: 'underline' }}
                                                >
                                                    <HStack spacing={1}>
                                                        <Icon as={MdBuild} boxSize={3} />
                                                        <Text fontSize="xs" fontWeight="600">
                                                            WO #{String(alert.work_order_id).length > 8 ? `${String(alert.work_order_id).substring(0, 8)}…` : String(alert.work_order_id)}
                                                        </Text>
                                                    </HStack>
                                                </ChakraLink>
                                            ) : (
                                                <Text fontSize="xs" color="gray.500">
                                                    N/A
                                                </Text>
                                            )}
                                        </Td>

                                        {/* Actions */}
                                        <Td>
                                            <HStack spacing={1}>
                                                <Tooltip label="View Details" placement="top" hasArrow>
                                                    <IconButton
                                                        icon={<MdVisibility />}
                                                        size="sm"
                                                        variant="ghost"
                                                        aria-label="View Details"
                                                        onClick={() => onViewDetails(alert)}
                                                        borderRadius="md"
                                                    />
                                                </Tooltip>

                                                {alert.status === 'active' && (
                                                    <Tooltip label="Acknowledge Alert" placement="top" hasArrow>
                                                        <IconButton
                                                            icon={<MdEditNote />}
                                                            size="sm"
                                                            variant="ghost"
                                                            colorScheme="blue"
                                                            aria-label="Acknowledge Alert"
                                                            onClick={() => onAcknowledge(alert.id)}
                                                            borderRadius="md"
                                                        />
                                                    </Tooltip>
                                                )}

                                                {(alert.status === 'active' || alert.status === 'acknowledged' || alert.status === 'work_order_created') && (
                                                    <Tooltip label="Resolve Alert" placement="top" hasArrow>
                                                        <IconButton
                                                            icon={<MdCheckCircle />}
                                                            size="sm"
                                                            variant="ghost"
                                                            colorScheme="green"
                                                            aria-label="Resolve Alert"
                                                            onClick={() => onResolve(alert.id)}
                                                            borderRadius="md"
                                                        />
                                                    </Tooltip>
                                                )}

                                                {/* More actions menu */}
                                                <Menu>
                                                    <MenuButton
                                                        as={IconButton}
                                                        icon={<MdMoreVert />}
                                                        size="sm"
                                                        variant="ghost"
                                                        aria-label="More actions"
                                                        borderRadius="md"
                                                    />
                                                    <MenuList>
                                                        <MenuItem icon={<MdContentCopy />} onClick={() => copyAlertDetails(alert)}>
                                                            Copy Details
                                                        </MenuItem>
                                                        <MenuItem icon={<MdFileDownload />} onClick={() => exportAlert(alert)}>
                                                            Export Alert
                                                        </MenuItem>
                                                    </MenuList>
                                                </Menu>
                                            </HStack>
                                        </Td>
                                    </MotionTr>
                                );
                            })}
                        </AnimatePresence>
                    </Tbody>
                </Table>
            </Box>

            {/* Footer stats */}
            <Flex justify="space-between" align="center" mt={3} px={2}>
                <Text fontSize="xs" color="gray.500">
                    Showing {sortedAlerts.length} alert{sortedAlerts.length !== 1 ? 's' : ''}
                </Text>
                {selectedAlerts.size > 0 && (
                    <Text fontSize="xs" color="blue.500" fontWeight="600">
                        {selectedAlerts.size} selected
                    </Text>
                )}
            </Flex>
        </Box>
    );
}

ActiveAlertsTable.propTypes = {
    alerts: PropTypes.array.isRequired,
    onAcknowledge: PropTypes.func.isRequired,
    onResolve: PropTypes.func.isRequired,
    onViewDetails: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    // --- UPDATE (Alert System AI)
    selectedIds: PropTypes.instanceOf(Set),
    onToggleSelect: PropTypes.func,
    onToggleSelectAll: PropTypes.func,
    triageById: PropTypes.object,
    highlightIds: PropTypes.instanceOf(Set),
    // --- END UPDATE
};

export default ActiveAlertsTable;